import { onSessionExpired } from "services/users/auth";
import Configuration from "../config";
import { logError } from "./logging";
import { ApiRequestError } from "./errors";

export const API_BASE_URL = Configuration.API_BASE_URL + "api";

export type RequestOptions = {
  rawResponse?: boolean;
  headers?: Record<string, string>;
  onUnauthorized?: (serverUserId: string | null) => void;
  credentials?: RequestCredentials;
};

// Nullish values are omitted.
// Arrays are comma-joined. By convention backend expects a single comma-separated
// value for list params by convention
export type QueryParamValue =
  | string
  | number
  | boolean
  | ReadonlyArray<string | number>
  | null
  | undefined;
export type QueryParams = Record<string, QueryParamValue>;

export const api = {
  get: <TResponse, TQueryParams extends QueryParams | void = QueryParams>(
    path: string,
    query?: TQueryParams | null,
    config?: RequestOptions
  ): Promise<TResponse> => {
    return sendRequest<TResponse>("GET", path, { query: query ?? null, options: config });
  },

  post: <TPayload, TResponse = void, TQueryParams extends QueryParams = QueryParams>(
    path: string,
    data?: TPayload,
    options?: WriteOptions<TQueryParams>
  ): Promise<TResponse> => {
    const { query, ...config } = options ?? {};
    return sendRequest<TResponse>("POST", path, { body: data, query, options: config });
  },

  put: <TPayload, TResponse = void, TQueryParams extends QueryParams = QueryParams>(
    path: string,
    data?: TPayload,
    options?: WriteOptions<TQueryParams>
  ): Promise<TResponse> => {
    const { query, ...config } = options ?? {};
    return sendRequest<TResponse>("PUT", path, { body: data, query, options: config });
  },

  patch: <TPayload, TResponse = void, TQueryParams extends QueryParams = QueryParams>(
    path: string,
    data?: TPayload,
    options?: WriteOptions<TQueryParams>
  ): Promise<TResponse> => {
    const { query, ...config } = options ?? {};
    return sendRequest<TResponse>("PATCH", path, { body: data, query, options: config });
  },

  delete: <TPayload, TResponse = void, TQueryParams extends QueryParams = QueryParams>(
    path: string,
    data?: TPayload,
    options?: WriteOptions<TQueryParams>
  ): Promise<TResponse> => {
    const { query, ...config } = options ?? {};
    return sendRequest<TResponse>("DELETE", path, { body: data, query, options: config });
  },
};

type WriteOptions<TQueryParams extends QueryParams = QueryParams> = RequestOptions & {
  query?: TQueryParams;
};

type FetchParams = {
  query?: QueryParams | string | null;
  body?: unknown;
  options?: RequestOptions;
};

async function sendRequest<TResponse>(
  method: string,
  path: string,
  { query, body, options = {} }: FetchParams = {}
): Promise<TResponse> {
  const {
    rawResponse = false,
    onUnauthorized = onSessionExpired,
    credentials = "include",
  } = options;

  const urlString = buildUrl(path, query);

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...options.headers,
  };

  let fetchBody: BodyInit | undefined;
  if (body !== undefined && body !== null) {
    const contentType = headers["Content-Type"];
    if (contentType && !contentType.includes("application/json")) {
      fetchBody = body as BodyInit;
    } else {
      fetchBody = JSON.stringify(body);
      if (!contentType) {
        headers["Content-Type"] = "application/json";
      }
    }
  }

  try {
    const response = await fetch(urlString, {
      method,
      headers,
      body: fetchBody,
      credentials,
    });

    if (response.ok) {
      if (rawResponse) {
        return response as unknown as TResponse;
      }

      const contentType = response.headers.get("content-type");
      if (!contentType?.includes("application/json")) {
        return null as unknown as TResponse;
      }

      // Both checks needed: a 204 never carries content-length (RFC 7230).
      if (response.status === 204 || response.headers.get("content-length") === "0") {
        return null as unknown as TResponse;
      }

      // Awaited so a parse failure is wrapped into ApiRequestError below.
      return (await response.json()) as TResponse;
    }

    throw response;
  } catch (error) {
    let wrappedError: ApiRequestError;
    if (error instanceof Response) {
      const errorResponseBody = await error
        .clone()
        .json()
        .catch(() => undefined); // empty callback necessary, .catch() with no handler is a no-op.
      wrappedError = new ApiRequestError(error, errorResponseBody);
      if (wrappedError.isUnauthorized) {
        // The server sends this header empty, not absent, when the request had no user.
        onUnauthorized(error.headers.get(USER_ID_HEADER) || null);
      }
    } else {
      wrappedError = new ApiRequestError(
        undefined,
        undefined,
        error instanceof Error ? error : new Error(String(error))
      );
    }

    if (!(error instanceof Response) || error.status >= 500) {
      logError(`Unexpected API request error: ${method} ${urlString}`, { err: error });
    }

    throw wrappedError;
  }
}

function buildUrl(path: string, query: QueryParams | string | null | undefined): string {
  const base = API_BASE_URL + path;
  if (!query) {
    return base;
  }
  const qs =
    typeof query === "string"
      ? query
      : new URLSearchParams(
          Object.entries(query)
            .filter(([, value]) => value !== null && value !== undefined)
            .map(([key, value]) => [
              key,
              Array.isArray(value) ? value.join() : String(value),
            ])
        ).toString();
  return qs ? `${base}?${qs}` : base;
}

const USER_ID_HEADER = "x-user-id";

export { HTTP_CODES } from "./errors";
